import { useState, useEffect } from 'react'

const MOBILE_BREAKPOINT = 768

const WORKSHEETS = [
  {
    file: 'talent-map.html',
    name: 'Talent Map',
    desc: 'Quarterly leadership-team conversation about every person on the team. Right seat? Lives the values? Trending up?',
    cadence: 'Quarterly',
  },
  {
    file: 'comp-frame.html',
    name: 'Comp Frame',
    desc: 'Rough leveling system and salary bands. Removes the "who asked loudest" problem.',
    cadence: 'Annual',
  },
  {
    file: 'quarterly-review.html',
    name: 'Quarterly Review',
    desc: 'One page. Four questions. Written by the report, discussed with the manager, signed by both.',
    cadence: 'Quarterly',
  },
  {
    file: 'how-to-work-with-me.html',
    name: 'How to Work With Me',
    desc: 'Your operating manual for the people who work with you. Update once a year — or when something changes.',
    cadence: 'Annual',
  },
  {
    file: 'weekly-one-on-one.html',
    name: 'Weekly 1:1',
    desc: 'Thirty minutes. Owned by the report. Their topics first. Never canceled.',
    cadence: 'Weekly',
  },
  {
    file: 'structured-interview-loop.html',
    name: 'Interview Debrief',
    desc: 'One per interviewer, per candidate. Written before the debrief meeting — no vote until everyone has submitted.',
    cadence: 'Per hire',
  },
  {
    file: 'investor-update.html',
    name: 'Investor / Board Update',
    desc: 'Monthly. One page. Send even when the month is bad — especially then.',
    cadence: 'Monthly',
  },
]

function WorksheetIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="3.5" y="2.5" width="15" height="17" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
      <path d="M7 7h8M7 10h8M7 13h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  )
}

function OpenIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M5 2H2v8h8V7M7 2h3v3M5 7l5-5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  )
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

export default function Toolbox() {
  const isMobile = useIsMobile()

  return (
    <div style={{ padding: isMobile ? 14 : 32, maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: isMobile ? 24 : 36 }}>
        <div style={{
          fontSize: 10, letterSpacing: '.25em', color: 'var(--hemlock-green)',
          fontWeight: 600, textTransform: 'uppercase', marginBottom: 8
        }}>
          The People Toolbox
        </div>
        <h1 style={{
          fontSize: isMobile ? 26 : 32, fontWeight: 400, color: 'var(--text-primary)',
          lineHeight: 1.15, marginBottom: 10, fontFamily: 'Georgia, "Cormorant Garamond", serif'
        }}>
          Tools for running the people side.
        </h1>
        <p style={{
          fontSize: 13, color: 'var(--text-muted)', maxWidth: 580, lineHeight: 1.6
        }}>
          Printable templates for the moments every founder hits as they scale —
          hiring, feedback, performance, comp, and the monthly investor email.
          Open any worksheet, fill it on screen or print it, use it with your team.
        </p>
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: isMobile ? 12 : 18,
      }}>
        {WORKSHEETS.map(w => (
          <a
            key={w.file}
            href={`/toolbox/${w.file}`}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'flex', flexDirection: 'column',
              background: 'var(--hemlock-white)',
              border: '1px solid #E5E1D6',
              borderRadius: 10,
              padding: isMobile ? 16 : 20,
              textDecoration: 'none',
              color: 'inherit',
              transition: 'border-color .15s, box-shadow .15s, transform .15s',
              minHeight: isMobile ? 'auto' : 168,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--hemlock-green)'
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(26,46,38,0.08)'
              e.currentTarget.style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#E5E1D6'
              e.currentTarget.style.boxShadow = 'none'
              e.currentTarget.style.transform = 'none'
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              marginBottom: 12, gap: 12
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'rgba(44,74,62,0.08)',
                color: 'var(--hemlock-green)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <WorksheetIcon />
              </div>
              <span style={{
                fontSize: 10, fontWeight: 500,
                color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '.08em',
                padding: '4px 8px',
                background: '#F7F4EF',
                borderRadius: 999,
                whiteSpace: 'nowrap',
              }}>
                {w.cadence}
              </span>
            </div>

            <div style={{
              fontSize: 17, fontWeight: 500, color: 'var(--text-primary)',
              marginBottom: 6, lineHeight: 1.3,
              fontFamily: 'Georgia, "Cormorant Garamond", serif',
            }}>
              {w.name}
            </div>
            <p style={{
              fontSize: 12.5, color: 'var(--text-secondary)',
              lineHeight: 1.55, marginBottom: 14, flex: 1,
            }}>
              {w.desc}
            </p>

            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, fontWeight: 500,
              color: 'var(--hemlock-green)',
              alignSelf: 'flex-start',
            }}>
              Open &amp; print
              <OpenIcon />
            </div>
          </a>
        ))}
      </div>

      {/* Footer note */}
      <div style={{
        marginTop: isMobile ? 24 : 36, padding: '14px 18px',
        background: 'rgba(44,74,62,0.04)',
        borderLeft: '2px solid var(--hemlock-green)',
        fontSize: 12, color: 'var(--text-secondary)',
        lineHeight: 1.6, borderRadius: '0 6px 6px 0',
      }}>
        <strong style={{ color: 'var(--text-primary)', fontWeight: 500 }}>How to use these.</strong>{' '}
        Open in a new tab, then print to PDF or send to a printer. Each one is a single-page template
        designed to be filled in by hand or annotated digitally. The discipline is in the writing —
        not the length.
      </div>
    </div>
  )
}
